import { useEffect, useState } from 'react'
import { Loader2, FileClock, Eye, CheckCircle2 } from 'lucide-react'
import { quotesApi, type QuoteVersionSummary } from '@api/client'
import type { Quote } from '@api/types'
import { formatCurrency, formatDate } from '@utils/format'
import { QuotePreviewModal } from '@/features/scope/components/QuotePreviewModal'

function nameFromEmail(email: string | null | undefined): string | null {
  if (!email) return null
  return email.split('@')[0].split('.').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
}

/**
 * "Versions" tab — lists the immutable snapshots captured each time this quote was
 * approved. Clicking one opens it read-only (QuotePreviewModal) so anyone can see exactly
 * what was signed off, at any point in time.
 */
export function QuoteVersionsTab({ dealId }: { dealId: number }) {
  const [versions, setVersions] = useState<QuoteVersionSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewing, setViewing] = useState<Quote | null>(null)
  const [loadingId, setLoadingId] = useState<number | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    quotesApi.listVersions(dealId).then((res) => {
      if (!alive) return
      if (res.success && res.data) {
        setVersions(res.data.versions)
        setError(null)
      } else {
        setError(res.error || 'Failed to load approved versions')
      }
      setLoading(false)
    })
    return () => { alive = false }
  }, [dealId])

  const openVersion = async (v: QuoteVersionSummary) => {
    setLoadingId(v.id)
    const res = await quotesApi.getVersion(dealId, v.id)
    setLoadingId(null)
    if (res.success && res.data?.quote) setViewing(res.data.quote)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading approved versions…
      </div>
    )
  }

  if (error) {
    return <div className="py-8 text-center text-red-400 text-sm">{error}</div>
  }

  if (versions.length === 0) {
    return (
      <div className="py-12 text-center text-[var(--text-muted)]">
        <FileClock className="mx-auto mb-2" size={28} />
        <p className="text-sm">No approved versions yet.</p>
        <p className="text-xs mt-1">A read-only snapshot is captured automatically each time this quote is approved.</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-[var(--text-muted)]">
        A snapshot is captured each time this quote is approved. Open any version to see exactly what was signed off.
      </p>
      {versions.map((v) => {
        const approver = v.approved_by_name || nameFromEmail(v.approved_by) || '—'
        return (
          <div
            key={v.id}
            className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg"
          >
            <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[var(--text-primary)]">
                Version {v.version_number}
                <span className="text-xs font-normal text-[var(--text-muted)] ml-2">
                  Approved {formatDate(v.approved_at)} by {approver}
                </span>
              </div>
              <div className="text-[11px] text-[var(--text-muted)] mt-0.5 flex flex-wrap gap-x-4">
                {v.tcv != null && <span>TCV {formatCurrency(v.tcv)}</span>}
                {v.arr != null && <span>ARR {formatCurrency(v.arr)}/yr</span>}
                {v.implementation_fee != null && <span>Impl fee {formatCurrency(v.implementation_fee)}</span>}
                {v.gross_profit_margin != null && <span>Margin {v.gross_profit_margin.toFixed(1)}%</span>}
              </div>
            </div>
            <button
              onClick={() => openVersion(v)}
              disabled={loadingId === v.id}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-xs font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50 flex-shrink-0"
            >
              {loadingId === v.id ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
              View
            </button>
          </div>
        )
      })}

      {viewing && <QuotePreviewModal quote={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}
